// @flow
import _ from 'lodash';

import { pluralize } from '@zapier/common-utils';

import FieldUtils from 'app/beta-editor/utils/FieldUtils';
import ServiceUtils from 'app/service/ServiceUtils';

import type { Field } from 'app/beta-editor/types';
import type { FlowNeedsMeta } from 'app/beta-editor/flowState';

const getReloadChoice = label => ({
  value: '',
  action: 'load-more-choices',
  label,
  isOpen: true,
  shouldShowInSearch: true,
  keepSearchString: true,
});

const reloadChoicesFunctions = {
  empty({ noun, defaultReloadLabel }) {
    return [
      {
        sectionKey: 'empty-choices-section',
        label: `Darn, we didn’t find any ${pluralize(
          noun,
          0
        )} in your account.`,
        initialState: 'open',
        shouldShowAtEnd: true,
      },
      getReloadChoice(defaultReloadLabel),
      {
        value: '',
        action: 'load-external-link',
        label: 'Learn how to fix this problem',
        link:
          '/help/troubleshoot/behavior/dropdown-menu-in-zap-field-is-empty-or-missing-options',
        isOpen: true,
        shouldShowInSearch: true,
        keepSearchString: true,
      },
    ];
  },
  pagingLoadMore({ choicesCount }) {
    return [
      getReloadChoice(
        `Don't see your choice here? Try loading more than ${choicesCount}…`
      ),
    ];
  },
  noPagingLoadMore({ defaultReloadLabel }) {
    return [getReloadChoice(defaultReloadLabel)];
  },
  noMoreChoices({ noMoreChoicesText }) {
    return [
      getReloadChoice(
        `Sorry, no more choices. We've loaded ${noMoreChoicesText}!`
      ),
    ];
  },
};

const getReloadChoicesType = ({
  areChoicesEmpty,
  canTryToLoadMoreChoices,
  hasPaging,
}) => {
  if (areChoicesEmpty) {
    return 'empty';
  } else if (hasPaging && canTryToLoadMoreChoices) {
    return 'pagingLoadMore';
  } else if (canTryToLoadMoreChoices) {
    return 'noPagingLoadMore';
  } else {
    return 'noMoreChoices';
  }
};

export const getReloadChoices = (
  field: Field,
  needsMeta: ?FlowNeedsMeta,
  choicesCount: number
) => {
  const pages = _.get(needsMeta, [field.key, 'pages']);
  const pageCount = pages && pages.length ? pages.length : 0;
  const hasEmptyLastPage =
    pageCount > 0 && pages[pages.length - 1].length === 0;

  // If we have an empty last page, then our last page load found
  // nothing. Otherwise, if we don't have an empty last page, we either
  // haven't tried to load yet, or we might still have more pages.
  const canTryToLoadMoreChoices = !hasEmptyLastPage;
  const areChoicesEmpty = choicesCount === 0;

  // This is probably silly. If we get one choice, we're probably not
  // going to find any more, but I'll work on heuristics in another PR.
  const noMoreChoicesText =
    choicesCount === 1 ? 'the only one' : `all ${choicesCount}`;

  const service = _.get(field, ['meta', 'service']);
  const triggerName = FieldUtils.splitPrefill(field.prefill).action;
  const trigger =
    (service && _.find(service.read, svc => svc.key === triggerName)) || {};
  const hasPaging = trigger && trigger.paging;
  const noun = (
    ServiceUtils.actionNoun(service, triggerName) || ''
  ).toLowerCase();
  const serviceName = (service && service.name) || 'the service';
  const defaultReloadLabel = `Check ${serviceName} & reload to bring in new choices.`;

  const reloadChoicesType = getReloadChoicesType({
    areChoicesEmpty,
    canTryToLoadMoreChoices,
    hasPaging,
  });
  return reloadChoicesFunctions[reloadChoicesType]({
    areChoicesEmpty,
    canTryToLoadMoreChoices,
    hasPaging,
    noMoreChoicesText,
    noun,
    defaultReloadLabel,
    choicesCount,
  });
};
